import React, { useState, useEffect } from 'react';
import { Form, Button, Image, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { validateResetToken, setNewPassword, forgotPassword } from '../services/api';
import { TOKEN_TYPES } from '../lib/utils/constant';
// --- SVG Icons ---
const EyeIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" /><path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" /></svg>);
const EyeSlashIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="m10.79 12.912-1.614-1.615a3.5 3.5 0 0 1-4.474-4.474l-2.06-2.06C.938 6.278 0 8 0 8s3 5.5 8 5.5a7.029 7.029 0 0 0 2.79-.588zM5.21 3.088A7.028 7.028 0 0 1 8 2.5c5 0 8 5.5 8 5.5s-.939 1.721-2.641 3.238l-2.062-2.062a3.5 3.5 0 0 0-4.474-4.474L5.21 3.089z" /><path d="M5.525 7.646a2.5 2.5 0 0 0 2.829 2.829l-2.83-2.829zm4.95.708-2.829-2.83a2.5 2.5 0 0 1 2.829 2.829zm3.171 6-12-12 .708-.708 12 12-.708.708z" /></svg>);

function SetPassword() {
    // 1. Grab the token from the URL (e.g., ?token=xyz)
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const token = searchParams.get('token');
    const hasForgotPasswordFlag = searchParams.has('forgotPassword') || location.hash.toLowerCase() === '#forgotpassword';
    // If no token is present, default to forgot-password flow.
    const isForgotPasswordMode = hasForgotPasswordFlag || !token;

    // State
    const [isValidating, setIsValidating] = useState(!isForgotPasswordMode);
    const [isTokenValid, setIsTokenValid] = useState(isForgotPasswordMode);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isInviteSent, setIsInviteSent] = useState(false);
    const [tokenType, setTokenType] = useState('');
    const navigate = useNavigate();

    // 2. API Call 1: Validate Token on Mount
    useEffect(() => {
        const validate = async () => {
            if (isForgotPasswordMode) {
                setIsTokenValid(true);
                setIsValidating(false);
                setError('');
                return;
            }

            if (!token) {
                setIsValidating(false);
                setError('Invalid link: No token provided.');
                return;
            }

            try {
                // Call your validation API
                const response = await validateResetToken(token);
                setTokenType(response?.data?.data?.tokenType);
                setIsTokenValid(true);
            } catch (err) {
                console.error(err);
                setError(err.response.data.message);
                setIsTokenValid(false);
            } finally {
                setIsValidating(false);
            }
        };

        validate();
    }, [token, isForgotPasswordMode]);

    // 3. API Call 2: Set Password
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        // --- PASSWORD VALIDATION LOGIC ---
        const validations = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%]/.test(password),
        };

        if (!validations.length) {
            setError("Password must be at least 8 characters long.");
            return;
        }
        if (!validations.uppercase) {
            setError("Password must contain at least one uppercase letter.");
            return;
        }
        if (!validations.lowercase) {
            setError("Password must contain at least one lowercase letter.");
            return;
        }
        if (!validations.number) {
            setError("Password must contain at least one number.");
            return;
        }
        if (!validations.special) {
            setError("Password must contain at least one special character.");
            return;
        }
        // ---------------------------------

        setIsSubmitting(true);
        try {
            await setNewPassword(token, password, confirmPassword, tokenType);
            setSuccess(true);
            // Optional: Redirect after a few seconds
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(typeof err === 'string' ? err : 'Failed to set password. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');

        const trimmedEmail = email.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!trimmedEmail) {
            setError('Email is required.');
            return;
        }
        if (!emailRegex.test(trimmedEmail)) {
            setError('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        try {
            await forgotPassword(trimmedEmail);
            setIsInviteSent(true);
        } catch (err) {
            setError(typeof err === 'string' ? err : 'Failed to send reset password link. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Render ---
    return (
        <div
            style={{
                backgroundImage: `url(/images/background_1.jpg)`, // Use your new image from /public
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem',
            }}
        >
            {/* === 2. "Focused" Login Card === */}
            <div
                style={{
                    maxWidth: '450px',
                    width: '100%',
                    padding: '2.5rem',
                    borderRadius: '1rem', // 16px
                    // Frosted glass effect
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
                }}
            >
                <div className="text-center mb-4">
                    <Image src="/images/logo.png" alt="AllWell Logo" style={{ height: '50px' }} />
                </div>

                <h2 className="fw-bold mb-2 text-center">{tokenType === TOKEN_TYPES.PASSWORD_RESET ? ' Reset Password' : 'Set Password'}</h2>

                {/* STATE 1: Validating Token */}
                {isValidating && (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-3 text-muted">Validating your secure link...</p>
                    </div>
                )}

                {/* STATE 2: Error (Invalid Token) */}
                {!isValidating && !isTokenValid && (
                    <div className="text-center">
                        <Alert variant="danger" className="mb-4">
                            {error}
                        </Alert>
                        <Button as={Link} to="/login" variant="primary">Back to Login</Button>
                    </div>
                )}

                {/* STATE 3: Success (Password Set) */}
                {!isValidating && isTokenValid && success && (
                    <div className="text-center">
                        <Alert variant="success" className="mb-4">
                            <h5 className="alert-heading h6">Success!</h5>
                            <p className="mb-0">{tokenType === TOKEN_TYPES.PASSWORD_RESET ? "Your password has been reset successfully. Redirecting to login..." : "Your password has been set successfully. Redirecting to login..."}</p>
                        </Alert>
                        <Button as={Link} to="/login" variant="primary" className="w-100">Go to Login</Button>
                    </div>
                )}

                {/* STATE 4: Forgot Password Form */}
                {!isValidating && isForgotPasswordMode && !isInviteSent && (
                    <Form onSubmit={handleForgotPassword}>
                        <p className="text-muted mb-4 text-center">Enter your email to receive a reset password link.</p>

                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form.Group className="mb-4" controlId="formForgotPasswordEmail">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <div className="d-grid">
                            <Button variant="primary" type="submit" size="lg" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <><Spinner as="span" animation="border" size="sm" className="me-2" />Sending Invite...</>
                                ) : 'Send Invite'}
                            </Button>
                        </div>
                    </Form>
                )}

                {!isValidating && isForgotPasswordMode && isInviteSent && (
                    <Alert variant="success" className="mb-0 text-center">
                        Reset password link has been sent to your email. You can close this tab.
                    </Alert>
                )}

                {/* STATE 5: Form (Set Password) */}
                {!isValidating && !isForgotPasswordMode && isTokenValid && !success && (
                    <Form onSubmit={handleSubmit}>
                        <p className="text-muted mb-4 text-center">Create a new password for your account.</p>

                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form.Group className="mb-3" controlId="formNewPassword">
                            <Form.Label>New Password</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter new password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                <InputGroup.Text onClick={() => setShowPassword(!showPassword)} style={{ cursor: 'pointer' }}>
                                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                </InputGroup.Text>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="formConfirmPassword">
                            <Form.Label>Confirm Password</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                />
                                <InputGroup.Text onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ cursor: 'pointer' }}>
                                    {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                </InputGroup.Text>
                            </InputGroup>
                        </Form.Group>

                        <div className="d-grid">
                            <Button variant="primary" type="submit" size="lg" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <><Spinner as="span" animation="border" size="sm" className="me-2" />Setting Password...</>
                                ) : 'Set Password'}
                            </Button>
                        </div>
                    </Form>
                )}
            </div>
        </div>
    );
}

export default SetPassword;